import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../../../apiClient";
import { useSelectedLocation } from "../../location-management/hooks/useSelectedLocation";
import { GET_EVENTS_QUERY_KEY } from "./useListEvents";

export interface CancelRecurringGroupOptions {
    futureOnly?: boolean;
    notifyParticipants?: boolean;
}

export interface CancelRecurringGroupResult {
    success: boolean;
    message: string;
    totalEvents: number;
    cancelledCount: number;
    eventsWithParticipants: number;
    skippedCount: number;
}

export const useCancelRecurringGroup = () => {
    const client = useApiClient();
    const queryClient = useQueryClient();
    const { selectedLocation } = useSelectedLocation();

    return useMutation({
        mutationKey: ['cancelRecurringGroup'],
        mutationFn: ({
            recurringGroupId,
            options = {}
        }: {
            recurringGroupId: string;
            options?: CancelRecurringGroupOptions
        }) => {
            const params = new URLSearchParams();
            if (options.futureOnly !== undefined) {
                params.append('futureOnly', options.futureOnly.toString());
            }
            if (options.notifyParticipants !== undefined) {
                params.append('notifyParticipants', options.notifyParticipants.toString());
            }

            return client.fetchApi<CancelRecurringGroupResult, {}>({
                endpoint: `events/recurring-group/${recurringGroupId}?${params.toString()}`,
                method: 'DELETE'
            });
        },
        onSuccess: (data, variables) => {
            // Invalidate all event lists to refetch and show updated state
            // This handles batch deletions where multiple events are removed at once
            queryClient.invalidateQueries({ queryKey: GET_EVENTS_QUERY_KEY(selectedLocation?.id, 'past') });
            queryClient.invalidateQueries({ queryKey: GET_EVENTS_QUERY_KEY(selectedLocation?.id, 'future') });
        }
    });
};
